// @flow
import { number, object } from 'yup';

import {
  AWS,
  CLUSTER_CLUSTER_LINKING_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_CONNECTORS_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_KAFKA_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_KSQLDB_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_PROVIDER_BACKEND_NAME,
  CLUSTER_RTCE_MONTHLY_INPUTS_JSON_CONFIG,
  RTCE_NUM_CCUS_BACKEND_NAME,
  RTCE_STORAGE_GB_BACKEND_NAME,
  CLUSTER_TABLEFLOW_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_TABLEFLOW_V2_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_TYPE_BACKEND_NAME,
  ENTERPRISE_CLUSTER_TYPE,
  FLINK_POOL_MONTHLY_INPUTS_JSON_CONFIG,
  MAX_INPUT_VALUE_FOR_ANY_FIELD,
  NATIVE_INFERENCE_POOL_CCU_MONTHLY_INPUTS_JSON_CONFIG,
  NATIVE_INFERENCE_POOL_TOKEN_BASED_MODELS_JSON_CONFIG,
  TABLEFLOW_VERSIONS,
} from '../../../constants';
import {
  getNonNegativeIntegerSchema,
  getNonNegativeNumberSchema,
} from '../../../common-utils/yup-utils';
import { getMonthName } from '../cluster-input-table/utils';
import { getTableflowVersion } from '../../../custom-hooks/useTableflowVersion';

export const getCkuThresholdConfig = (metaData, clusterType) =>
  metaData.cluster_configs.cku_threshold_configs.find(
    (config) =>
      config.cluster_type === clusterType && config.is_extended_partition_limit_enabled === false
  );

export const getTableflowThresholdConfig = (metaData, clusterType) =>
  metaData.tableflow_configs.tableflow_threshold_configs.find(
    (config) => config.cluster_type === clusterType
  );

const validateThatWriteThroughputProvidedIsWithinKafkaWriteLimit = (
  value,
  context,
  fieldLabel,
  isForKafka,
  metaData
) => {
  const { createError, path, from } = context;
  const clusterInputs = from[3].value;
  const clusterType = clusterInputs[CLUSTER_TYPE_BACKEND_NAME];

  // For Kafka,
  // In case of Enterprise Cluster, write_throughput_avg_mbps is not relevant and hence DO NOT validate
  // Relevant ones for Kafka are :  write_throughput_avg_mbps_pl &  write_throughput_avg_mbps_pni and their sum should be validated against the limit

  // For Tableflow, we should validate the value against (E) - the value which we have used to validate sum of write_throughput_avg_mbps_pl and write_throughput_avg_mbps_pni
  // And that E comes out to be CLUSTER_WRITE_THROUGHPUT_LIMITS_MAP[Enterprise]

  if (isForKafka && clusterType === ENTERPRISE_CLUSTER_TYPE) {
    return true;
  }

  const writeThroughputLimit = getCkuThresholdConfig(
    metaData,
    clusterType
  ).write_throughput_threshold;
  const errorMessage = `${fieldLabel} must be less than or equal to ${writeThroughputLimit}`;

  const isError = value > writeThroughputLimit;
  if (isError) {
    return createError({
      message: errorMessage,
      path: path,
    });
  }

  return true;
};

const getWriteThroughputValidationSchema = (metaData) => {
  return number()
    .required()
    .min(0)
    .max(MAX_INPUT_VALUE_FOR_ANY_FIELD)
    .label('This field')
    .typeError('Required Field')
    .test('max', 'Write Throughput must be within the limits', (value, context) =>
      validateThatWriteThroughputProvidedIsWithinKafkaWriteLimit(
        value,
        context,
        'Write Throughput',
        true,
        metaData
      )
    );
};
const getReadThroughputValidationSchema = (metaData) => {
  return number()
    .required()
    .min(0)
    .max(MAX_INPUT_VALUE_FOR_ANY_FIELD)
    .label('This field')
    .typeError('Required Field')
    .test(
      'max',
      'Read Throughput must be within the limits',
      (value, { createError, path, from }) => {
        const clusterInputs = from[3].value;
        const clusterType = clusterInputs[CLUSTER_TYPE_BACKEND_NAME];

        // In case of Enterprise Cluster, read_throughput_avg_mbps is not relevant and hence DO NOT validate
        // Relevant ones are:  read_throughput_avg_mbps_pl &   read_throughput_avg_mbps_pni and their sum should be validated against the limit
        if (clusterType === ENTERPRISE_CLUSTER_TYPE) {
          return true;
        }

        const readThroughputLimit = getCkuThresholdConfig(
          metaData,
          clusterType
        ).read_throughput_threshold;
        const isError = value > readThroughputLimit;
        if (isError) {
          return createError({
            message: `Read Throughput must be less than or equal to ${readThroughputLimit}`,
            path: path,
          });
        }
        return true;
      }
    );
};

const getEnterpriseWriteThroughputValidationSchema = (metaData) => {
  return number()
    .min(0)
    .max(MAX_INPUT_VALUE_FOR_ANY_FIELD)
    .label('This field')
    .typeError('Required Field')
    .test(
      'total-limit',
      'Total Average Write Throughput must be within the limits',
      (value, { createError, path, from, parent }) => {
        const clusterInputs = from[3].value;
        const clusterType = clusterInputs[CLUSTER_TYPE_BACKEND_NAME];

        // In case of a non-Enterprise Cluster, write_throughput_avg_mbps_pl &  write_throughput_avg_mbps_pni are not relevant and hence DO NOT validate
        if (clusterType !== ENTERPRISE_CLUSTER_TYPE) {
          return true;
        }

        // Get Private Link value (always considered for all cluster types)
        const privateLinkValue = parent?.write_throughput_avg_mbps_pl ?? 0;

        // Get PNI value (only for AWS clusters)
        const clusterProvider = clusterInputs[CLUSTER_PROVIDER_BACKEND_NAME];
        const pniValue = clusterProvider === AWS ? parent?.write_throughput_avg_mbps_pni ?? 0 : 0;

        const total = privateLinkValue + pniValue;
        const writeThroughputLimit = getCkuThresholdConfig(
          metaData,
          clusterType
        ).write_throughput_threshold;

        const isError = total > writeThroughputLimit;
        if (isError) {
          return createError({
            message: `Total Average Write Throughput ${
              clusterProvider === AWS ? '(Private Link + PNI)' : '(Private Link)'
            } must be less than or equal to ${writeThroughputLimit}`,
            path: path,
          });
        }
        return true;
      }
    );
};

const getEnterpriseReadThroughputValidationSchema = (metaData) => {
  return number()
    .min(0)
    .max(MAX_INPUT_VALUE_FOR_ANY_FIELD)
    .label('This field')
    .typeError('Required Field')
    .test(
      'total-limit',
      'Total Average Read Throughput must be within the limits',
      (value, { createError, path, from, parent }) => {
        const clusterInputs = from[3].value;
        const clusterType = clusterInputs[CLUSTER_TYPE_BACKEND_NAME];

        // In case of a non-Enterprise Cluster, read_throughput_avg_mbps_pl &  read_throughput_avg_mbps_pni are not relevant and hence DO NOT validate
        if (clusterType !== ENTERPRISE_CLUSTER_TYPE) {
          return true;
        }

        // Get Private Link value (always considered for all cluster types)
        const privateLinkValue = parent?.read_throughput_avg_mbps_pl ?? 0;

        // Get PNI value (only for AWS clusters)
        const clusterProvider = clusterInputs[CLUSTER_PROVIDER_BACKEND_NAME];
        const pniValue = clusterProvider === AWS ? parent?.read_throughput_avg_mbps_pni ?? 0 : 0;

        const total = privateLinkValue + pniValue;
        const readThroughputLimit = getCkuThresholdConfig(
          metaData,
          clusterType
        ).read_throughput_threshold;

        const isError = total > readThroughputLimit;
        if (isError) {
          return createError({
            message: `Total Average Read Throughput ${
              clusterProvider === AWS ? '(Private Link + PNI)' : '(Private Link)'
            } must be less than or equal to ${readThroughputLimit}`,
            path: path,
          });
        }
        return true;
      }
    );
};

// A threshold of -1 means the limit is not applicable for this rate card / cluster type
// (mirrors the backend's own check in validation_utils.go) - so the max check is skipped.
const NOT_APPLICABLE_THRESHOLD = -1;

const getConnectedClientCountValidationSchema = (metaData) => {
  return number()
    .integer()
    .required()
    .min(0)
    .max(MAX_INPUT_VALUE_FOR_ANY_FIELD)
    .label('This field')
    .typeError('Required Field')
    .test(
      'max',
      'Connected Clients Count must be within the limits',
      (value, { createError, path, from }) => {
        const clusterInputs = from[3].value;
        const clusterType = clusterInputs[CLUSTER_TYPE_BACKEND_NAME];
        const connectedClientCountLimit = getCkuThresholdConfig(
          metaData,
          clusterType
        ).connected_clients_threshold;

        if (connectedClientCountLimit === NOT_APPLICABLE_THRESHOLD) {
          return true;
        }

        const isError = value > connectedClientCountLimit;
        if (isError) {
          return createError({
            message: `Connected Clients Count must be less than or equal to ${connectedClientCountLimit}`,
            path: path,
          });
        }
        return true;
      }
    );
};

const getClientConnectionAttemptsPerSecSchema = (metaData) => {
  return number()
    .integer()
    .required()
    .min(0)
    .max(MAX_INPUT_VALUE_FOR_ANY_FIELD)
    .label('This field')
    .typeError('Required Field')
    .test(
      'max',
      'Client Connection Attempts must be within the limits',
      (value, { createError, path, from }) => {
        const clusterInputs = from[3].value;
        const clusterType = clusterInputs[CLUSTER_TYPE_BACKEND_NAME];
        const connectionAttemptsLimit = getCkuThresholdConfig(
          metaData,
          clusterType
        ).connection_attempts_threshold;

        if (connectionAttemptsLimit === NOT_APPLICABLE_THRESHOLD) {
          return true;
        }

        const isError = value > connectionAttemptsLimit;
        if (isError) {
          return createError({
            message: `Client Connection Attempts Count must be less than or equal to ${connectionAttemptsLimit}`,
            path: path,
          });
        }
        return true;
      }
    );
};

const getClientRequestsPerSecSchema = (metaData) => {
  return number()
    .integer()
    .required()
    .min(0)
    .max(MAX_INPUT_VALUE_FOR_ANY_FIELD)
    .label('This field')
    .typeError('Required Field')
    .test(
      'max',
      'Client Requests must be within the limits',
      (value, { createError, path, from }) => {
        const clusterInputs = from[3].value;
        const clusterType = clusterInputs[CLUSTER_TYPE_BACKEND_NAME];
        const clientRequestsLimit = getCkuThresholdConfig(
          metaData,
          clusterType
        ).requests_per_sec_threshold;

        if (clientRequestsLimit === NOT_APPLICABLE_THRESHOLD) {
          return true;
        }

        const isError = value > clientRequestsLimit;
        if (isError) {
          return createError({
            message: `Client Requests must be less than or equal to ${clientRequestsLimit}`,
            path: path,
          });
        }
        return true;
      }
    );
};

const getKafkaValidationSchema = (metaData) =>
  object({
    write_throughput_peak_mbps: number()
      .min(0)
      .max(MAX_INPUT_VALUE_FOR_ANY_FIELD)
      .label('This field')
      .typeError('Required Field')
      .required(),
    read_throughput_peak_mbps: number()
      .min(0)
      .max(MAX_INPUT_VALUE_FOR_ANY_FIELD)
      .label('This field')
      .typeError('Required Field')
      .required(),
    write_throughput_avg_mbps: getWriteThroughputValidationSchema(metaData),
    read_throughput_avg_mbps: getReadThroughputValidationSchema(metaData),
    connected_client_count: getConnectedClientCountValidationSchema(metaData),
    client_connection_attempts_per_sec: getClientConnectionAttemptsPerSecSchema(metaData),
    client_requests_per_sec: getClientRequestsPerSecSchema(metaData),
    write_throughput_avg_mbps_pl: getEnterpriseWriteThroughputValidationSchema(metaData),
    write_throughput_avg_mbps_pni: getEnterpriseWriteThroughputValidationSchema(metaData),
    read_throughput_avg_mbps_pl: getEnterpriseReadThroughputValidationSchema(metaData),
    read_throughput_avg_mbps_pni: getEnterpriseReadThroughputValidationSchema(metaData),
  });

const getKsqlDBValidationSchema = (metaData) =>
  object({
    csu_count: number()
      .integer()
      .min(0)
      .max(metaData.cluster_configs.ksql_threshold_configs.ksql_count_threshold)
      .label('This field')
      .typeError('Required Field'),
  });

const getFlinkPoolValidationSchema = (metaData) =>
  object({
    cfu_count: number()
      .min(0)
      .max(metaData.flink_pool_config.cfu_count_threshold)
      .label('This field')
      .typeError('Required Field'),
  });

const nativeInferencePoolCcuValidationSchema = object({
  ccu_count: getNonNegativeIntegerSchema(),
});

const nativeInferencePoolTokenBasedModelValidationSchema = object({
  input_tokens: getNonNegativeIntegerSchema(),
  output_tokens: getNonNegativeIntegerSchema(),
});

const clusterLinkingValidationSchema = object({
  link_count: number()
    .integer()
    .min(0)
    .max(MAX_INPUT_VALUE_FOR_ANY_FIELD)
    .label('This field')
    .typeError('Required Field'),
  avg_link_read_mbps: number()
    .min(0)
    .max(MAX_INPUT_VALUE_FOR_ANY_FIELD)
    .label('This field')
    .typeError('Required Field'),
  avg_link_write_mbps: number()
    .min(0)
    .max(MAX_INPUT_VALUE_FOR_ANY_FIELD)
    .label('This field')
    .typeError('Required Field'),
});

const getConnectorsValidationSchema = (metaData) =>
  object({
    task_count: number()
      .integer()
      .min(0)
      .max(metaData.cluster_configs.connector_threshold_configs.connector_count_threshold)
      .label('This field')
      .typeError('Required Field'),
    throughput_average_mbps: number()
      .min(0)
      .max(metaData.cluster_configs.connector_threshold_configs.connector_throughput_threshold)
      .label('This field')
      .typeError('Required Field'),
    num_cores: number()
      .integer()
      .min(0)
      .max(MAX_INPUT_VALUE_FOR_ANY_FIELD)
      .label('This field')
      .typeError('Required Field'),
  });

const getTableflowNumTopicsValidationSchema = () =>
  number()
    .min(0)
    .max(MAX_INPUT_VALUE_FOR_ANY_FIELD)
    .label('This field')
    .typeError('Required Field');

const getTableflowThroughputValidationSchema = (fieldLabel, metaData) =>
  number()
    .required()
    .min(0)
    .max(MAX_INPUT_VALUE_FOR_ANY_FIELD)
    .label('This field')
    .typeError('Required Field')
    .test(
      'max',
      `${fieldLabel} must be within the cluster write throughput limits`,
      (value, context) =>
        validateThatWriteThroughputProvidedIsWithinKafkaWriteLimit(
          value,
          context,
          fieldLabel,
          false,
          metaData
        )
    );

const getTableflowDataProcessedValidationSchema = () =>
  number()
    .min(0)
    .max(MAX_INPUT_VALUE_FOR_ANY_FIELD)
    .label('This field')
    .typeError('Required Field');

const getTableflowValidationSchema = (metaData) =>
  object({
    num_topics: getTableflowNumTopicsValidationSchema().test(
      'max',
      'Number of Topics must be within the limits',
      (value, { createError, path, from }) => {
        const clusterInputs = from[3].value;
        const clusterType = clusterInputs[CLUSTER_TYPE_BACKEND_NAME];
        const { num_topics_threshold: numTopicsThreshold } = getTableflowThresholdConfig(
          metaData,
          clusterType
        );

        const isError = value > numTopicsThreshold;
        if (isError) {
          return createError({
            message: `Number of Topics must be less than or equal to ${numTopicsThreshold}`,
            path: path,
          });
        }
        return true;
      }
    ),
    avg_throughput_mbps: getTableflowThroughputValidationSchema(
      'Average Throughput',
      metaData
    ).test(
      'tableflow-max',
      'Average Throughput must be within the tableflow limits',
      (value, { createError, path, from }) => {
        const clusterInputs = from[3].value;
        const clusterType = clusterInputs[CLUSTER_TYPE_BACKEND_NAME];
        const { avg_throughput_threshold: avgThroughputThreshold } = getTableflowThresholdConfig(
          metaData,
          clusterType
        );

        const isError = value > avgThroughputThreshold;
        if (isError) {
          return createError({
            message: `Average Throughput must be less than or equal to ${avgThroughputThreshold}`,
            path: path,
          });
        }
        return true;
      }
    ),
  });

const getTableflowV2ValidationSchema = (metaData) =>
  object({
    num_topics: getTableflowNumTopicsValidationSchema(),
    data_processed_gb: getTableflowDataProcessedValidationSchema(),
    index_data_processed_gb: getTableflowDataProcessedValidationSchema(),
    avg_throughput_mbps: getTableflowThroughputValidationSchema(
      'Tableflow Kafka Throughput',
      metaData
    ),
    storage_gb: getTableflowDataProcessedValidationSchema(),
  });

// Only one of {tableflow, tableflow_v2} is ever seeded with real months for a given estimate
// (see ClusterTopLevelContainerForm), but both are always registered in CLUSTER_DETAILS_CONFIG
// so validation exists for whichever is active. The inactive key's months stay {}, so it must
// not be validated against required fields, or the form is permanently invalid/unsavable.
const noOpMonthValidationSchema = object();

const rtceValidationSchema = object({
  [RTCE_NUM_CCUS_BACKEND_NAME]: getNonNegativeNumberSchema().integer(),
  [RTCE_STORAGE_GB_BACKEND_NAME]: getNonNegativeNumberSchema(),
});

const getSchemaForMonth = (inputType, estimate, metaData) => {
  switch (inputType) {
    case CLUSTER_KAFKA_MONTHLY_INPUTS_JSON_CONFIG:
      return getKafkaValidationSchema(metaData);
    case CLUSTER_KSQLDB_MONTHLY_INPUTS_JSON_CONFIG:
      return getKsqlDBValidationSchema(metaData);
    case CLUSTER_CLUSTER_LINKING_MONTHLY_INPUTS_JSON_CONFIG:
      return clusterLinkingValidationSchema;
    case CLUSTER_CONNECTORS_MONTHLY_INPUTS_JSON_CONFIG:
      return getConnectorsValidationSchema(metaData);
    case FLINK_POOL_MONTHLY_INPUTS_JSON_CONFIG:
      return getFlinkPoolValidationSchema(metaData);
    case NATIVE_INFERENCE_POOL_CCU_MONTHLY_INPUTS_JSON_CONFIG:
      return nativeInferencePoolCcuValidationSchema;
    case NATIVE_INFERENCE_POOL_TOKEN_BASED_MODELS_JSON_CONFIG:
      return nativeInferencePoolTokenBasedModelValidationSchema;
    case CLUSTER_TABLEFLOW_MONTHLY_INPUTS_JSON_CONFIG:
      return getTableflowVersion(estimate) === TABLEFLOW_VERSIONS.V2
        ? noOpMonthValidationSchema
        : getTableflowValidationSchema(metaData);
    case CLUSTER_TABLEFLOW_V2_MONTHLY_INPUTS_JSON_CONFIG:
      return getTableflowVersion(estimate) === TABLEFLOW_VERSIONS.V2
        ? getTableflowV2ValidationSchema(metaData)
        : noOpMonthValidationSchema;
    case CLUSTER_RTCE_MONTHLY_INPUTS_JSON_CONFIG:
      return rtceValidationSchema;
    default:
      throw 'unknown input type provided';
  }
};

export const getMonthlyInputsValidationFuncForType =
  (inputType) =>
  ({ estimate, metaData }) => {
    const numberOfMonths = estimate.deal_duration.deal_duration_months;
    const schemaObject = {};
    for (let i = 1; i <= numberOfMonths; i++) {
      schemaObject[getMonthName(i)] = getSchemaForMonth(inputType, estimate, metaData);
    }
    return object({ months: object(schemaObject) });
  };
